// 📄 catalogoProgramasService.ts
// Catálogo global de Programas/Includes ABAP (custom o estándar) usados en
// las integraciones SAP Ariba / S/4HANA — mismo patrón exacto que
// catalogoBadisService.ts (CRUD completo solo desde el panel Admin, lectura
// abierta a cualquier consultor autenticado, ver firestore.rules colección
// `catalogoProgramas`).
//
// Se relaciona con BAdIs y Transacciones vía referencias N:N
// (badiRefIds/transaccionRefIds) — antes "Programa" era solo un `tipo`
// dentro del catálogo de BAdIs (ver TIPOS_OBJETO_ABAP en
// catalogoBadisService.ts); se absorbió acá como catálogo propio para
// poder describir un programa con más detalle y sus relaciones reales.
import {
  doc,
  collection,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp
} from "firebase/firestore";
import { db } from '../firebase';
import { SolucionBadi, SOLUCIONES_BADI, solucionBadiMeta } from './catalogoBadisService.ts';

export { SOLUCIONES_BADI as SOLUCIONES_PROGRAMA, solucionBadiMeta as solucionProgramaMeta };
export type { SolucionBadi as SolucionPrograma };

export interface ProgramaData {
  nombre: string;
  nombreTecnico: string; // ej. LMLSRF0F, ZREP_ARBCIG_RECON
  solucion: SolucionBadi;
  descripcion: string;
  badiRefIds: string[]; // referencias N:N a catalogoBadis
  transaccionRefIds: string[]; // referencias N:N a catalogoTransacciones
}

export interface Programa extends ProgramaData {
  id: string;
  createdAt?: any;
  updatedAt?: any;
}

const CATALOGO_COLLECTION = "catalogoProgramas";

export const getProgramas = async (): Promise<Programa[]> => {
  const snap = await getDocs(collection(db, CATALOGO_COLLECTION));
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      nombre: data.nombre || '',
      nombreTecnico: data.nombreTecnico || '',
      solucion: (data.solucion as SolucionBadi) || 'general',
      descripcion: data.descripcion || '',
      badiRefIds: data.badiRefIds || [],
      transaccionRefIds: data.transaccionRefIds || [],
      createdAt: data.createdAt,
      updatedAt: data.updatedAt
    };
  });
};

/**
 * Crea (programaId = null) o actualiza (programaId = id existente) un
 * registro. Igual que saveBadi, el id lo genera el llamador cuando es
 * nuevo (ver generarId en ProgramaCatalogSection.tsx).
 */
export const savePrograma = async (programaId: string, data: ProgramaData, esNuevo: boolean): Promise<void> => {
  if (!data.nombre.trim()) throw new Error("El nombre del programa es obligatorio.");

  const payload: Record<string, any> = {
    nombre: data.nombre.trim(),
    nombreTecnico: data.nombreTecnico.trim(),
    solucion: data.solucion,
    descripcion: data.descripcion.trim(),
    badiRefIds: data.badiRefIds,
    transaccionRefIds: data.transaccionRefIds,
    updatedAt: serverTimestamp()
  };
  if (esNuevo) payload.createdAt = serverTimestamp();

  await setDoc(doc(db, CATALOGO_COLLECTION, programaId), payload, { merge: true });
};

export const deletePrograma = async (programaId: string): Promise<void> => {
  await deleteDoc(doc(db, CATALOGO_COLLECTION, programaId));
};
